/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import { clamp } from 'lodash';
import React from 'react';
import ReactResizeDetector from 'react-resize-detector';

import {
  ResizerStyles,
  resizableHandleStyle,
} from 'src/components/common/resizerStyles/ResizerStyles';
import { useLocalStorage } from 'src/hooks/useLocalStorage';

import { SplitPane } from './splitPane/SplitPane';

export type ThreePaneLayoutInfo = ReturnType<typeof useThreeLayoutInfo>;
export function useThreeLayoutInfo({
  leftOpenKey,
  leftPanelWidthKey,
  rightPanelWidthKey,
  leftMinWidth,
  centerMinWidth,
  dividerWidth,
  rightMinWidth,
}: {
  leftOpenKey: 'preflightSnippetsOpen';
  leftPanelWidthKey: 'preflightSnippetWidth';
  rightPanelWidthKey: 'preflightOutputWidth';
  leftMinWidth: number;
  centerMinWidth: number;
  dividerWidth: number;
  rightMinWidth: number;
}) {
  const centerAndRightMinWidth =
    centerMinWidth + dividerWidth * 2 + rightMinWidth;

  const [leftPaneOpen, setLeftPaneOpen] = useLocalStorage(leftOpenKey);

  const [containerWidth, setContainerWidth] = React.useState(
    () => window.innerWidth,
  );
  const [leftPanelSavedWidth, setLeftPanelWidth] =
    useLocalStorage(leftPanelWidthKey);
  const [rightPanelWidth, setRightPanelWidth] =
    useLocalStorage(rightPanelWidthKey);

  const leftPanelOpenWidth = Math.min(
    leftPanelSavedWidth,
    containerWidth - centerAndRightMinWidth,
  );
  const centerAndRightWidth = containerWidth - leftPanelOpenWidth;
  const leftPanelWidth = leftPaneOpen ? leftPanelOpenWidth : 0;

  return React.useMemo(
    () => ({
      dividerWidth,
      centerMinWidth,
      rightMinWidth,
      centerAndRightMinWidth,
      leftMinWidth,
      leftPaneOpen,
      setLeftPaneOpen,
      leftPanelWidth,
      setLeftPanelWidth,
      rightPanelWidth,
      setRightPanelWidth,
      centerAndRightWidth,
      setContainerWidth,
    }),
    [
      dividerWidth,
      centerMinWidth,
      rightMinWidth,
      centerAndRightMinWidth,
      leftMinWidth,
      leftPaneOpen,
      leftPanelWidth,
      centerAndRightWidth,
      rightPanelWidth,
      setLeftPaneOpen,
      setLeftPanelWidth,
      setRightPanelWidth,
    ],
  );
}

export const ThreePaneLayout = ({
  layoutInfo: {
    dividerWidth,
    centerMinWidth,
    rightMinWidth,
    centerAndRightMinWidth,
    leftMinWidth,
    leftPaneOpen,
    leftPanelWidth,
    setLeftPanelWidth,
    rightPanelWidth,
    setRightPanelWidth,
    centerAndRightWidth,
    setContainerWidth,
  },

  leftPanel,
  centerPanel,
  rightPanel,
  className,
}: {
  layoutInfo: ThreePaneLayoutInfo;
  leftPanel: React.ReactNode;
  centerPanel: React.ReactNode;
  rightPanel: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={classnames(className, 'relative flex flex-1 flex-col')}
      css={css(resizableHandleStyle)}
    >
      <ResizerStyles />
      <ReactResizeDetector
        handleWidth
        onResize={(w) => {
          if (w) setContainerWidth(w);
        }}
      >
        <SplitPane
          allowResize={leftPaneOpen}
          className="flex py-2"
          primary="first"
          minSize={leftMinWidth}
          maxSize={-centerAndRightMinWidth}
          size={leftPanelWidth}
          onChange={setLeftPanelWidth}
        >
          <div className="flex h-full flex-1 flex-col">
            {leftPaneOpen ? leftPanel : null}
          </div>
          <SplitPane
            className="flex"
            primary="second"
            allowResize
            minSize={rightMinWidth}
            maxSize={-(centerMinWidth + dividerWidth)}
            size={clamp(
              rightPanelWidth,
              rightMinWidth,
              centerAndRightWidth - centerMinWidth - dividerWidth * 2,
            )}
            onChange={(size) => setRightPanelWidth(size)}
          >
            {centerPanel}
            {rightPanel}
          </SplitPane>
        </SplitPane>
      </ReactResizeDetector>
    </div>
  );
};
